import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import {getCreditFees} from 'wobi-web-common/dist/components/utils/paymentRestrictions';

const PRODUCT_CATEGORY_GROUPS = ['casco', 'mandatory'];

const formatAmount = (amount) => `₪${Math.round(amount).toLocaleString()}`;

const styles = {
  line: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    py: 0.75,
  },
  label: (theme) => ({
    fontSize: theme.typography.pxToRem(15),
    color: 'text.primary',
  }),
  amount: (theme) => ({
    fontSize: theme.typography.pxToRem(15),
    color: 'text.primary',
    direction: 'ltr',
    fontVariantNumeric: 'tabular-nums',
  }),
  total: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    pt: 2,
  },
  totalLabel: (theme) => ({
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 700,
    color: 'text.primary',
  }),
  totalAmount: (theme) => ({
    fontSize: theme.typography.pxToRem(36),
    fontWeight: 800,
    color: 'secondary.main',
    direction: 'ltr',
    lineHeight: 1,
  }),
  disclaimer: (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    color: 'text.secondary',
    mt: 1,
  }),
};

/**
 * A summary line: the label and its amount.
 */
const SummaryLine = ({label, amount}) => (
  <Box sx={styles.line}>
    <Typography sx={styles.label}>{label}</Typography>
    <Typography sx={styles.amount}>{formatAmount(amount)}</Typography>
  </Box>
);

SummaryLine.propTypes = {
  amount: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

/**
 * The policy price breakdown with the credit fee of the chosen payments and the total to pay.
 * The credit fee is for display only, the insurance company charges it.
 */
const PaymentSummary = ({
  isThirdParty = false,
  cascoPrice = 0,
  mandatoryPrice = 0,
  serviceFee = 0,
  otherProducts = [],
  restrictions = {},
  payments = {},
}) => {
  const intl = useIntl();
  const m = (id, values) => intl.formatMessage({id}, values);
  const productCategoryGroupNames = {
    casco: m(isThirdParty ? 'payments.summary.product_category_group.third_party' : 'payments.summary.product_category_group.casco'),
    mandatory: m('payments.summary.product_category_group.mandatory'),
  };
  const prices = {casco: cascoPrice, mandatory: mandatoryPrice};
  // the insurance company charges interest after the interest free payments, riders have none
  const creditFees = getCreditFees(prices, restrictions, payments);
  const total = cascoPrice + mandatoryPrice + serviceFee +
    otherProducts.reduce((sum, product) => sum + product.price, 0) +
    creditFees.reduce((sum, {fee}) => sum + fee, 0);

  return (
    <Box>
      {PRODUCT_CATEGORY_GROUPS.filter((productCategoryGroup) => prices[productCategoryGroup] > 0).map((productCategoryGroup) => (
        <SummaryLine
          key={productCategoryGroup}
          label={m('payments.summary.insurance', {productCategoryGroup: productCategoryGroupNames[productCategoryGroup]})}
          amount={prices[productCategoryGroup]} />
      ))}
      {otherProducts.map((product) => (
        <SummaryLine key={product.name} label={product.name} amount={product.price} />
      ))}
      {serviceFee > 0 && (
        <SummaryLine label={m('payments.summary.service_fee')} amount={serviceFee} />
      )}
      {creditFees.map(({productCategoryGroup, fee, payments: productCategoryGroupPayments}) => (
        <SummaryLine
          key={`credit-fee-${productCategoryGroup}`}
          label={m('payments.summary.credit_fee', {
            payments: productCategoryGroupPayments,
            productCategoryGroup: productCategoryGroupNames[productCategoryGroup],
          })}
          amount={fee} />
      ))}
      <Divider sx={{mt: 1}} />
      <Box sx={styles.total}>
        <Typography sx={styles.totalLabel}>{m('payments.summary.total')}</Typography>
        <Typography sx={styles.totalAmount}>{formatAmount(total)}</Typography>
      </Box>
      <Typography sx={styles.disclaimer}>
        {`${m('payments.summary.annual_premium')} ${m('offers.payment_disclaimer')}.`}
      </Typography>
    </Box>
  );
};

PaymentSummary.propTypes = {
  cascoPrice: PropTypes.number,
  isThirdParty: PropTypes.bool,
  mandatoryPrice: PropTypes.number,
  otherProducts: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    price: PropTypes.number,
  })),
  payments: PropTypes.shape({
    casco: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mandatory: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  restrictions: PropTypes.shape({
    casco: PropTypes.object,
    mandatory: PropTypes.object,
  }),
  serviceFee: PropTypes.number,
};

export default PaymentSummary;
